type IuguCreditCard = {
  valid: () => boolean
}

type IuguPaymentTokenResponse = {
  id?: string
  errors?: unknown
}

type IuguApi = {
  setAccountID: (accountId: string) => void
  setTestMode: (enabled: boolean) => void
  CreditCard: (
    number: string,
    month: string,
    year: string,
    firstName: string,
    lastName: string,
    verificationValue: string
  ) => IuguCreditCard
  createPaymentToken: (
    creditCard: IuguCreditCard,
    callback: (response: IuguPaymentTokenResponse) => void
  ) => void
}

export type IuguCardInput = {
  number: string
  expirationMonth: string
  expirationYear: string
  cvv: string
  holderName: string
}

function getIugu(): IuguApi {
  const iugu = (window as Window & { Iugu?: IuguApi }).Iugu
  if (!iugu) {
    throw new Error(
      'Biblioteca Iugu não carregada. Recarregue a página e tente novamente.'
    )
  }
  return iugu
}

function splitHolderName(holderName: string): {
  firstName: string
  lastName: string
} {
  const parts = holderName.trim().split(/\s+/).filter(Boolean)
  const firstName = parts[0] || 'Cliente'
  const lastName = parts.slice(1).join(' ') || firstName
  return { firstName, lastName }
}

/**
 * Tokeniza o cartão no browser via iugu.js (PCI). Retorna o payment token de uso único.
 */
export async function createIuguPaymentToken(
  card: IuguCardInput
): Promise<string> {
  const accountId = process.env.REACT_APP_IUGU_ACCOUNT_ID
  if (!accountId) {
    throw new Error(
      'Configuração de pagamento ausente (REACT_APP_IUGU_ACCOUNT_ID).'
    )
  }

  const iugu = getIugu()
  iugu.setAccountID(accountId)
  iugu.setTestMode(
    ['true', '1'].includes(
      String(process.env.REACT_APP_IUGU_TEST_MODE || '').toLowerCase()
    )
  )

  const number = String(card.number || '').replace(/\D/g, '')
  const month = String(card.expirationMonth || '')
    .replace(/\D/g, '')
    .padStart(2, '0')
    .slice(-2)
  let year = String(card.expirationYear || '').replace(/\D/g, '')
  if (year.length === 2) {
    year = `20${year}`
  }
  const cvv = String(card.cvv || '').replace(/\D/g, '')
  const { firstName, lastName } = splitHolderName(card.holderName)

  const creditCard = iugu.CreditCard(
    number,
    month,
    year,
    firstName,
    lastName,
    cvv
  )

  if (typeof creditCard.valid === 'function' && !creditCard.valid()) {
    throw new Error('Dados do cartão inválidos. Revise número, validade e CVV.')
  }

  return new Promise((resolve, reject) => {
    iugu.createPaymentToken(creditCard, (response) => {
      if (response?.id) {
        resolve(response.id)
        return
      }
      reject(
        new Error(
          'Não foi possível tokenizar o cartão. Revise os dados e tente novamente.'
        )
      )
    })
  })
}
